/**
 * Service for managing user-specific invitations
 * @author Vue Frontend Team
 */
import ApiService from './api.service';

// Types for user invitations
export interface UserInvitation {
  id: string | number;
  projectId: string;
  projectName: string;
  inviterName: string;
  inviterEmail: string;
  accessRights: string[];
  invitationDate: string;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED';
  expirationDate?: string;
}

export interface InvitationResponse {
  success: boolean;
  message: string;
  invitation?: UserInvitation;
}

export interface UserInvitationListResponse {
  invitations: UserInvitation[];
  totalCount: number;
}

class UserInvitationsService extends ApiService {
  constructor() {
    super({
      showNotifications: true,
      notificationOptions: {
        title: 'User Invitations Error'
      }
    });
  }

  /**
   * Get all pending invitations for the current user
   * @returns Promise that resolves with the user's pending invitations
   */
  async getPendingInvitations(): Promise<UserInvitationListResponse | null> {
    return this.get('/api/user/invitations/pending');
  }

  /**
   * Accept an invitation with OTP verification
   * @param invitationId - The ID of the invitation
   * @param otp - The OTP code received by email
   * @returns Promise that resolves when invitation is accepted
   */
  async acceptInvitation(invitationId: string | number, otp: string): Promise<InvitationResponse | null> {
    if (!invitationId || !otp) {
      return Promise.reject({ message: 'Invitation ID and OTP are required' });
    }

    return this.post(`/api/user/invitations/${invitationId}/accept`, {
      otp: otp
    });
  }

  /**
   * Decline an invitation
   * @param invitationId - The ID of the invitation
   * @returns Promise that resolves when invitation is declined
   */
  async declineInvitation(invitationId: string | number): Promise<InvitationResponse | null> {
    if (!invitationId) {
      return Promise.reject({ message: 'Invitation ID is required' });
    }

    return this.post(`/api/user/invitations/${invitationId}/decline`, {});
  }
}

export default new UserInvitationsService();
